import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { BackButton } from "@/hooks/useBackNavigation";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NextActionCard } from "@/components/NextActionCard";
import { WhatHappensNextPanel } from "@/components/WhatHappensNextPanel";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { TestBadge, LifecycleBadge, ApptBadge, TaskBadge, IntentBadge, BookingStateBadge, CalSyncBadge, CommStatusBadge, commLabel, commMetaDetails, EmptyState, VoiceStateBadge } from "@/components/common";
import { QuickActions, CasesTab, TicketsTab, CallsTab, CommsTab, ProfileTab, LeadFormInfo, WebinarsTab } from "@/components/C360Extras";
import { DocumentsWorkspace } from "@/components/DocumentsWorkspace";
import { FinanceTab, NotesTab, ApprovalRequests } from "@/components/FinanceC360";
import { ConsultationActions } from "@/components/ConsultationActions";
import { AdmissionOutcomeActions } from "@/components/AdmissionOutcomeActions";
import { SalesMentorPanel, MentorSnapshotTeaser } from "@/components/SalesMentorPanel";
import { CallLogDialog } from "@/pages/CallsPage";
import { TaskReviewBanner } from "@/components/TaskReviewPrompt";
import { TagsPanel } from "@/components/TagsPanel";
import { MergeCustomerAction } from "@/components/CustomerMerge";
import { WorkflowSetupRequiredNotice, isWorkflowSetupBlock } from "@/components/WorkflowSetupRequired";
import { StageGateExplanation } from "@/components/StageGateExplanation";
import { gateView, clientGateView } from "@/lib/stageGate";
import { pickNextTask } from "@/lib/nextTask";
import { CalendarClock, Link2, Sparkles, MessageSquare, History, ChevronRight, Phone, Mail, MapPin, AlertTriangle, Send, Check, SkipForward, RotateCcw, ThumbsUp, ThumbsDown, Bot } from "lucide-react";
import { useSlotPicker, useSingleFlight } from "@/hooks/useSlotPicker";
import { SlotPickerView } from "@/components/SlotPickerView";

const WH_DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

// Plain-language recommended action for a non-overridable stage-gate
// blocker (pipeline.StageTransitionError.overridable == false) — the
// backend already routes the 3 blocker types with a real dedicated dialog
// (required_call_outcome/required_consultation_outcome/required_admission_
// outcome) around this generic dialog entirely; what lands here is always
// one that has no such dedicated flow, or a mixed set.
const BLOCKER_TYPE_RECOMMENDED_ACTION = {
  required_consultation_booked: "Book a real consultation for this opportunity first.",
  required_approval: "This requires a recorded Leadership approval — see Approvals.",
};

// UX-only mirror of the backend's real SYSTEM_VERIFIABLE_TASK_TYPES
// (routers_crm.py) — see TasksPage.jsx's own copy for the full rationale.
const SYSTEM_VERIFIABLE_TASK_TYPES = new Set([
  "call_outcome", "consultation_outcome", "consultation_booked", "admission_outcome",
  "human_followup", "followup_call", "payment_verification", "invoice_generation",
]);

const formatOfficeHours = (schedule) => {
  if (!schedule || !Object.keys(schedule).length) return "";
  const order = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const open = order.filter((d) => schedule[d]);
  const closed = order.filter((d) => !schedule[d]);
  const openPart = open.map((d) => `${WH_DAY_LABELS[d]} ${schedule[d].open}–${schedule[d].close}`).join(", ");
  const closedPart = closed.length ? `${closed.map((d) => WH_DAY_LABELS[d]).join("/")} closed` : "";
  return `Office hours: ${[openPart, closedPart].filter(Boolean).join(". ")} IST.`;
};

const BookDialog = ({ opp, open, setOpen, onBooked }) => {
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [date, setDate] = useState("");
  const [officeHours, setOfficeHours] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/appointments/types").then((r) => {
      setTypes(r.data.filter((t) => t.active));
    });
    // Canonical resolution (backend, not a second React copy of the same
    // rule) — falls back to the product's family/mother appointment type
    // when a commercial child product (SM_INHOUSE/SM_MARA/SA_PREMIUM/
    // SA_PARTNERSHIP) has none of its own.
    if (opp.product_code) {
      api.get(`/appointments/resolve-type?product_code=${opp.product_code}`).then((r) => {
        if (r.data.appointment_type) setTypeId(r.data.appointment_type.appointment_type_id);
      });
    }
    api.get("/admin/working-hours").then((r) => setOfficeHours(formatOfficeHours(r.data))).catch(() => setOfficeHours(""));
    setDate("");
  }, [open, opp]);

  // R15 U1: the chosen slot belongs to the exact (type, date, product) it was fetched for; changing any of them invalidates it at once, late responses are ignored, and the status
  // says why there is nothing to pick (closed day vs not permitted vs could not load).
  const picker = useSlotPicker({
    key: open && typeId && date ? `${typeId}|${date}|${opp.product_code || ""}|${opp.opportunity_id}` : null, date,
    load: async () => (await api.get(`/appointments/slots?appointment_type_id=${typeId}&date_str=${date}`)).data,
  });

  const [book, busy] = useSingleFlight(async () => {
    if (!picker.slot) return;
    try {
      await api.post("/appointments", { opportunity_id: opp.opportunity_id, appointment_type_id: typeId, start: picker.slot });
      toast.success("Consultation booked");
      setOpen(false);
      onBooked();
    } catch (e) {
      // the server re-checks the slot and the seat; if it refused (taken in the meantime / not a legal slot) say so and refresh what is really available
      toast.error(errDetail(e));
      picker.reload();
    }
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Book consultation</DialogTitle>
          <DialogDescription>{officeHours || "Loading office hours…"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Appointment type</Label>
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger data-testid="book-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.appointment_type_id} value={t.appointment_type_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input data-testid="book-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          {picker.status !== "idle" && (
            <div className="space-y-1.5">
              <Label>Available slots (IST)</Label>
              <SlotPickerView picker={picker} idPrefix="book" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="book-confirm-button" disabled={!picker.slot || busy} onClick={book}>{busy ? "Booking…" : "Confirm booking"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Leadership-only, OPPORTUNITY-SPECIFIC controls — reuses the exact same
   backend endpoints as Approvals/Unassigned's Claim (routers_crm.assign_lead,
   atomic) and Calls' existing Mark Lost (routers_crm.change_lifecycle) —
   no parallel assignment/Lost system. crm.assign (already manager+supervisor
   -scoped) gates Assign; stage.override (already manager+supervisor-scoped,
   reused rather than inventing a new capability) gates Mark Lost here
   specifically — Calls' own existing Mark Lost stays gated by stage.change
   exactly as before, unchanged. */
const OpportunityLeadershipActions = ({ opp, contact, onChanged }) => {
  const { hasCap } = useAuth();
  const [assignOpen, setAssignOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [target, setTarget] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [lostInitiatedBy, setLostInitiatedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeReasons, setRemoveReasons] = useState([]);
  const [removeCheck, setRemoveCheck] = useState(null);
  const [removeReasonCode, setRemoveReasonCode] = useState("");
  const [removeNote, setRemoveNote] = useState("");

  const canAssign = hasCap("crm.assign");
  const canMarkLost = hasCap("stage.override");
  if (!opp || opp.lifecycle_status !== "active" || (!canAssign && !canMarkLost)) return null;

  const openAssign = () => {
    setTarget(""); setAssignReason("");
    api.get("/users").then((r) => setEmployees(r.data.users.filter((u) =>
      u.is_active && ["counsellor", "supervisor", "manager", "process", "admin"].includes(u.role))))
      .catch(() => {});
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post(`/leads/assign/${opp.opportunity_id}`, {
        user_id: target, reason: assignReason || undefined,
        expected_current_owner_user_id: opp.owner_user_id || null,
      });
      toast.success("Assigned — a follow-up task was created for them");
      setAssignOpen(false); onChanged();
    } catch (e) {
      // A 409 here means the owner changed since this dialog opened
      // (someone else claimed/assigned it concurrently) — surfaced as a
      // clear conflict, never silently overwritten.
      toast.error(errDetail(e));
    } finally { setBusy(false); }
  };

  const submitLost = async () => {
    if (!lostReason.trim() || !lostInitiatedBy) return;
    setBusy(true);
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/lifecycle`,
        { status: "closed_lost", reason: lostReason, closure_initiated_by: lostInitiatedBy });
      toast.success("Marked Lost — history and analytics preserved");
      setLostOpen(false); setLostReason(""); setLostInitiatedBy(""); onChanged();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const openRemove = () => {
    setRemoveReasonCode(""); setRemoveNote(""); setRemoveCheck(null);
    api.get("/opportunities/removal-reasons").then((r) => setRemoveReasons(r.data)).catch(() => {});
    api.get(`/opportunities/${opp.opportunity_id}/discontinue-check`).then((r) => setRemoveCheck(r.data)).catch(() => {});
    setRemoveOpen(true);
  };

  const submitRemove = async () => {
    if (!removeReasonCode || (removeReasonCode === "other" && !removeNote.trim())) return;
    setBusy(true);
    try {
      await api.post(`/opportunities/${opp.opportunity_id}/discontinue`,
        { reason_code: removeReasonCode, note: removeNote.trim() || null });
      toast.success("Product removed — history preserved");
      setRemoveOpen(false);
      onChanged(opp.opportunity_id); // may have removed the SELECTED opportunity — reselect if so
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 text-xs flex-wrap" data-testid={`opp-leadership-actions-${opp.opportunity_id}`}>
      <span className="text-muted-foreground">Owner: {opp.owner_name || "Unassigned"}</span>
      {canAssign && (
        <Button size="sm" variant="outline" className="h-7 text-xs"
          data-testid={`opp-assign-btn-${opp.opportunity_id}`} onClick={openAssign}>Assign</Button>
      )}
      {canMarkLost && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]"
          data-testid={`opp-marklost-btn-${opp.opportunity_id}`}
          onClick={() => { setLostReason(""); setLostInitiatedBy(""); setLostOpen(true); }}>Mark Lost</Button>
      )}
      {canMarkLost && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground hover:text-foreground"
          data-testid={`opp-remove-btn-${opp.opportunity_id}`} onClick={openRemove}>Remove product</Button>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="opp-assign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Assign — {opp.product_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Current owner: {opp.owner_name || "Unassigned"}. This changes ONLY the {opp.product_name} opportunity
              for {contact?.full_name} — any other product/opportunity for this customer is unaffected.
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Assign to</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="opp-assign-target"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>{employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reason / note (optional)</Label>
              <Textarea data-testid="opp-assign-reason" value={assignReason} onChange={(e) => setAssignReason(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="opp-assign-confirm" disabled={!target || busy} onClick={submitAssign}>{busy ? "Assigning…" : "Assign"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lostOpen} onOpenChange={(o) => { setLostOpen(o); if (!o) { setLostReason(""); setLostInitiatedBy(""); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="opp-marklost-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[hsl(var(--danger))]">Mark Lost — {opp.product_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              This closes ONLY the {opp.product_name} opportunity for {contact?.full_name} as Lost. Any other
              product/opportunity for this customer stays exactly as it is. History and analytics are preserved.
            </p>
            <Textarea data-testid="opp-marklost-reason" placeholder="Lost reason / remarks (required)"
              value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
            <div className="space-y-1">
              <Label className="text-xs">Who decided not to proceed? (required)</Label>
              <Select value={lostInitiatedBy} onValueChange={setLostInitiatedBy}>
                <SelectTrigger data-testid="lost-initiated-by"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer decided not to proceed</SelectItem>
                  <SelectItem value="aussie_pathways">Aussie Pathways declined this customer</SelectItem>
                  <SelectItem value="mutual_other">Mutual / other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="opp-marklost-confirm" variant="destructive" disabled={!lostReason.trim() || !lostInitiatedBy || busy}
              onClick={submitLost}>{busy ? "Saving…" : "Confirm Lost"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="opp-remove-dialog">
          <DialogHeader><DialogTitle className="font-heading">Remove product — {opp.product_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              This discontinues ONLY the {opp.product_name} opportunity for {contact?.full_name}. It is removed from
              active views; any calls, tasks, consultations, documents or history stay fully intact. Any other
              product/opportunity for this customer is unaffected.
            </p>
            {removeCheck?.finance_blocked && (
              <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5 p-2 text-xs text-[hsl(var(--danger))]"
                data-testid="opp-remove-finance-blocked">
                {removeCheck.block_reason}
              </div>
            )}
            {removeCheck && !removeCheck.finance_blocked && removeCheck.has_activity && (
              <p className="text-[11px] text-muted-foreground" data-testid="opp-remove-activity-note">
                This opportunity has recorded activity ({removeCheck.dependent_counts.interactions} interaction(s),{" "}
                {removeCheck.dependent_counts.tasks} task(s), {removeCheck.dependent_counts.appointments} consultation(s)
                {removeCheck.dependent_counts.has_process_case ? ", an active Process Case" : ""}) — all of it is preserved.
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Reason (required)</Label>
              <Select value={removeReasonCode} onValueChange={setRemoveReasonCode} disabled={removeCheck?.finance_blocked}>
                <SelectTrigger data-testid="opp-remove-reason"><SelectValue placeholder="Why is this being removed?" /></SelectTrigger>
                <SelectContent>
                  {removeReasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note {removeReasonCode === "other" ? "(required)" : "(optional)"}</Label>
              <Textarea data-testid="opp-remove-note" placeholder="Additional details…" value={removeNote}
                onChange={(e) => setRemoveNote(e.target.value)} disabled={removeCheck?.finance_blocked} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="opp-remove-confirm" variant="destructive"
              disabled={busy || removeCheck?.finance_blocked || !removeReasonCode
                || (removeReasonCode === "other" && !removeNote.trim())}
              onClick={submitRemove}>{busy ? "Removing…" : "Confirm removal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StageStepper = ({ opp, contact, onChanged }) => {
  const { hasCap } = useAuth();
  const [pendingStage, setPendingStage] = useState(null);
  const [gateError, setGateError] = useState(null);
  // R13 Workstream 7: shared persistent explanation of a refused move.
  const [gateExplain, setGateExplain] = useState(null);
  const [gateAction, setGateAction] = useState(null); // {label, run}
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [callBlocker, setCallBlocker] = useState(null); // stageId
  const [consultBlocker, setConsultBlocker] = useState(null); // {stageId, appt}
  const [admissionBlocker, setAdmissionBlocker] = useState(null); // {stageId, opportunityId}
  const [backwardPrompt, setBackwardPrompt] = useState(null); // stageId
  const [backwardReason, setBackwardReason] = useState("");
  const [setupBlock, setSetupBlock] = useState(null); // R4 workflow_setup_required detail

  const move = async (stageId, override = false, moveReason = null) => {
    const stage = (opp.stages || []).find((s) => s.stage_id === stageId);
    // Always send whatever real reason is available — an override reason
    // (the shared `reason` state) or a backward-correction reason (passed
    // explicitly by the caller, e.g. the backward-reason-prompt dialog
    // below) — never gated on `override` alone any more, since a plain
    // (non-override) backward correction now also requires one.
    const effectiveReason = moveReason || (override ? reason : undefined);
    // 2026-09 R4 canonical lifecycle: the Case the board positions this
    // opportunity by (e.g. a Partnership Case from Agreed to Proceed on).
    const linkedCaseId = opp.canonical_case_id || opp.signup_case_id;
    setSetupBlock(null);
    try {
      if ((stage?.case_stage || stage?.category === "process") && linkedCaseId) {
        // Move within the Case's own workflow (registry gates, and the
        // no-bypass "Workflow setup required" block for an unmapped Case).
        await api.post(`/cases/${linkedCaseId}/stage`,
          { stage: stage.name, override, reason: effectiveReason });
      } else if (stage?.category === "process") {
        if (stage.board_activation || (!stage.case_stage && !stage.synthetic)) {
          // The real "Process / Handover" boundary — activates/attaches
          // the Case and advances the opportunity's own stage in one call.
          await api.post("/cases", { contact_id: opp.contact_id, opportunity_id: opp.opportunity_id,
                                     product_code: opp.product_code, override,
                                     reason: effectiveReason });
        } else {
          const entry = (opp.stages || []).find((s) => s.board_activation)?.name || "Process / Handover";
          setGateExplain(clientGateView({
            subject: contact?.full_name, requested: stage?.name,
            current: (opp.stages || []).find((s) => s.stage_id === (opp.unified_current_stage_id || opp.current_stage_id))?.name,
            message: "This opportunity hasn't entered Process yet.",
            nextAction: `Move to "${entry}" first - that is where the Case is activated.`}));
          return;
        }
      } else if (stage?.synthetic) {
        toast.error(`"${stage.name}" isn't set up on this database's pipeline yet — an administrator must reconcile the canonical lifecycle first.`);
        return;
      } else {
        await api.post(`/opportunities/${opp.opportunity_id}/stage`,
          { stage_id: stageId, override, reason: effectiveReason });
      }
      toast.success("Stage updated");
      setGateError(null); setPendingStage(null); setReason("");
      setCallBlocker(null); setConsultBlocker(null); setAdmissionBlocker(null);
      setBackwardPrompt(null); setBackwardReason("");
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const blockerType = d && typeof d === "object" ? d.blocker_type : null;
      const gctx = {
        requested: stage?.name, subject: contact?.full_name,
        current: (opp.stages || []).find((s) => s.stage_id === (opp.unified_current_stage_id || opp.current_stage_id))?.name };
      if (e?.response?.status === 409 && isWorkflowSetupBlock(d)) {
        // 2026-09 R4 — unmapped Case: distinct notice, no override, never a
        // generic toast.
        setSetupBlock(d);
      } else if (e?.response?.status === 409 && blockerType === "backward_reason_required") {
        // Authorized backward correction — the backend has already
        // confirmed no forward-exit evidence is being asked for here, it
        // just needs a real reason. Never the generic Override Gate
        // dialog (this isn't an override of anything).
        setBackwardPrompt(stageId); setBackwardReason("");
      } else if (e?.response?.status === 409 && blockerType === "required_call_outcome") {
        setCallBlocker(stageId);
      } else if (e?.response?.status === 409 && blockerType === "required_consultation_outcome") {
        const apptId = d.context?.appointment_id;
        if (apptId) {
          setGateExplain(gateView(e, gctx));
          setGateAction({ label: "Record the consultation outcome", run: () =>
            api.get(`/appointments/${apptId}`).then((r) => setConsultBlocker({ stageId, appt: r.data }))
              .catch(() => toast.error("Could not open the consultation outcome form.")) });
        } else {
          setGateExplain(gateView(e, gctx));
        }
      } else if (e?.response?.status === 409 && blockerType === "required_admission_outcome") {
        // Routes straight into the canonical Admission Outcome dialog
        // (POST /opportunities/{id}/admission-outcome) instead of a
        // generic "mandatory tasks" override prompt — same pattern as
        // the required_consultation_outcome branch above.
        setAdmissionBlocker({ stageId, opportunityId: d.context?.opportunity_id || opp.opportunity_id });
      } else if (e?.response?.status === 409 && d?.unmet_tasks) {
        setGateError(d); setPendingStage(stageId);
      } else {
        setGateExplain(gateView(e, gctx));
      }
    }
  };

  if (!opp.stages) return null;
  // unified_current_stage_id (P1) is the opportunity's own current_stage_id
  // normally, or — once it's crossed into Process — the synthetic
  // "process:<name>" key matching its Case's own current_stage, which is
  // the real position from that point on (the opportunity's own stage
  // freezes at the Process/Handover boundary).
  const currentIdx = opp.stages.findIndex(
    (s) => s.stage_id === (opp.unified_current_stage_id || opp.current_stage_id));
  // Proactive entry point (in addition to the gate-triggered one above) —
  // visible whenever the opportunity actually sits at the "Admission"
  // stage, so an employee can record the outcome without first attempting
  // (and being blocked on) a stage move. Same canonical mechanism either
  // way; this is just a second door into the identical dialog/state.
  const atAdmissionStage = opp.lifecycle_status === "active" && opp.stages[currentIdx]?.name === "Admission";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 items-center" data-testid="stage-stepper">
        {opp.stages.map((s, i) => (
          <button
            key={s.stage_id}
            data-testid={`stage-step-${s.order}`}
            onClick={() => opp.lifecycle_status === "active" && i !== currentIdx && move(s.stage_id)}
            className={`text-xs px-2 py-1 rounded-md border transition-colors ${
              i === currentIdx ? "bg-primary text-primary-foreground border-primary font-medium"
                : i < currentIdx ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
                  : "bg-card text-muted-foreground hover:bg-muted"
            }`}
          >
            {s.name}
          </button>
        ))}
        {atAdmissionStage && (
          <AdmissionOutcomeActions opportunityId={opp.opportunity_id} contactName={contact?.full_name}
            onSaved={onChanged} />
        )}
      </div>
      {setupBlock && (
        <WorkflowSetupRequiredNotice detail={setupBlock} attempted testId="c360-workflow-setup-required" />
      )}
      <Dialog open={!!gateError} onOpenChange={(o) => !o && setGateError(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" /> Stage gate blocked
            </DialogTitle>
            <DialogDescription>Mandatory tasks for the current stage are incomplete:</DialogDescription>
          </DialogHeader>
          <ul className="text-sm list-disc pl-5 space-y-1" data-testid="gate-unmet-tasks">
            {gateError?.unmet_tasks?.map((t) => <li key={t.task_id}>{t.title}</li>)}
          </ul>
          {/* `overridable` is the backend's own single source of truth
              (pipeline.py) for whether a stage.override holder + reason
              could ever clear THIS specific blocker — never re-derived
              here, so this can never drift into showing a working-looking
              Override control for a genuinely hard-blocked/mixed-evidence
              blocker (the exact bug this replaces: required_consultation_
              booked and a mixed evidence set used to fall through to this
              generic dialog with the button rendered but silently
              rejected on every click). */}
          {gateError && !gateError.overridable ? (
            <p className="text-sm text-muted-foreground" data-testid="gate-not-overridable">
              This stage requirement cannot be overridden.{" "}
              {BLOCKER_TYPE_RECOMMENDED_ACTION[gateError.blocker_type] ||
                "Complete the listed task(s) through their real workflow first."}
            </p>
          ) : (
            hasCap("stage.override") && (
              <div className="space-y-2">
                <Separator />
                <Label>Override with reason (audited)</Label>
                <Textarea data-testid="gate-override-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this gate being overridden?" />
              </div>
            )
          )}
          <DialogFooter>
            {gateError?.overridable && hasCap("stage.override") && (
              <Button data-testid="gate-override-button" variant="destructive" disabled={!reason.trim()} onClick={() => move(pendingStage, true)}>
                Override gate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <StageGateExplanation view={gateExplain} action={gateAction}
        onClose={() => { setGateExplain(null); setGateAction(null); }}
        onOpenCase={(id) => { setGateExplain(null); navigate(`/process/${id}`); }} />
      <Dialog open={!!backwardPrompt} onOpenChange={(o) => !o && setBackwardPrompt(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Move backward</DialogTitle>
            <DialogDescription>
              This is an authorized correction, not an override — the requirements for the
              stage you're leaving don't need to be complete. A reason is still required and audited.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="backward-move-reason" value={backwardReason}
            onChange={(e) => setBackwardReason(e.target.value)}
            placeholder="Why is this opportunity moving backward?" />
          <DialogFooter>
            <Button data-testid="backward-move-confirm" disabled={!backwardReason.trim()}
              onClick={() => move(backwardPrompt, false, backwardReason)}>
              Move backward
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {callBlocker && (
        <CallLogDialog
          row={{ contact_id: opp.contact_id, contact_name: contact?.full_name,
                opportunity_id: opp.opportunity_id, product_code: opp.product_code,
                owner_claim_status: "pending_outcome" }}
          open={!!callBlocker} setOpen={(o) => !o && setCallBlocker(null)}
          onLogged={() => move(callBlocker)} />
      )}
      {consultBlocker && (
        <ConsultationActions appt={consultBlocker.appt} forceOpen hideTriggerButtons
          onSaved={(saved) => {
            // 2026-09 R13 — `saved` is undefined for the dialog's own
            // unrelated "Update" intent-save action (not the required
            // outcome); ignore those entirely. Only a genuinely qualifying
            // outcome (isConversion) retries the originally-blocked move —
            // saving Not Ready/Lost/a reschedule/a cancellation must never
            // resume a stale attempt toward the stage that was first
            // clicked; it clears the blocker and refreshes instead.
            if (!saved) return;
            if (saved.isConversion) { move(consultBlocker.stageId); }
            else { setConsultBlocker(null); onChanged(); }
          }} />
      )}
      {admissionBlocker && (
        <AdmissionOutcomeActions opportunityId={admissionBlocker.opportunityId} contactName={contact?.full_name}
          forceOpen hideTriggerButtons
          onSaved={() => move(admissionBlocker.stageId)} />
      )}
    </div>
  );
};

const AiFeedbackRow = ({ output, onGiven }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const existing = output.feedback;

  const send = async (helpful) => {
    setSending(true);
    try {
      await api.post(`/ai/outputs/${output.ai_output_id}/feedback`, { helpful, note: note.trim() || null });
      toast.success("Feedback saved — the AI will factor this in next time");
      setNoteOpen(false); setNote("");
      onGiven();
    } catch (e) { toast.error(errDetail(e)); } finally { setSending(false); }
  };

  if (existing && !noteOpen) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span data-testid="ai-feedback-status">
          {existing.helpful === true ? "You marked this accurate" : existing.helpful === false ? "You flagged this as inaccurate" : "Feedback recorded"}
          {existing.note && ` — "${existing.note}"`}
        </span>
        <Button data-testid="ai-feedback-change" variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setNoteOpen(true)}>Change</Button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-muted-foreground">Was this accurate?</span>
        <Button data-testid="ai-feedback-helpful" variant="outline" size="sm" className="h-6 px-2" disabled={sending} onClick={() => send(true)}>
          <ThumbsUp className="h-3 w-3" />
        </Button>
        <Button data-testid="ai-feedback-not-helpful" variant="outline" size="sm" className="h-6 px-2" disabled={sending} onClick={() => send(false)}>
          <ThumbsDown className="h-3 w-3" />
        </Button>
      </div>
      <Input data-testid="ai-feedback-note" placeholder="Optional correction — e.g. what the AI got wrong (helps future summaries)"
        className="h-7 text-xs" value={note} onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && send(existing?.helpful ?? null)} />
    </div>
  );
};

const AiPanel = ({ contactId, opp, aiOutputs, onRefresh }) => {
  const [busy, setBusy] = useState(null);
  const summary = aiOutputs?.find((o) => o.type === "summary");

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "summary") await api.post(`/ai/summary/${contactId}`);
      else await api.post(`/ai/intent/${opp.opportunity_id}`);
      toast.success("AI insight generated");
      onRefresh();
    } catch (e) {
      toast.error(errDetail(e, "AI unavailable — CRM continues normally"));
    } finally { setBusy(null); }
  };

  return (
    <Card data-testid="ai-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--info))]" /> AI insights
          <Badge variant="outline" className="text-[10px]">Advisory only</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {summary ? (
          summary.content_status === "legacy_corrupted" ? (
            <p className="text-sm text-muted-foreground italic" data-testid="ai-summary-corrupted">
              This older AI content cannot be displayed safely. Regenerate it to create a verified version.
            </p>
          ) : (
            <div className="space-y-1.5">
              <p data-testid="ai-summary-text" className="text-sm whitespace-pre-wrap">{summary.content}</p>
              <p className="text-[10px] text-muted-foreground">Generated {fmtIST(summary.created_at)} · {summary.model} · AI can be wrong — verify against records</p>
              <AiFeedbackRow output={summary} onGiven={onRefresh} />
            </div>
          )
        ) : (
          <p className="text-xs text-muted-foreground">No AI summary yet. AI output never changes verified CRM data.</p>
        )}
        {opp?.intent_ai && (
          <div className="text-sm space-y-1" data-testid="ai-intent-block">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">AI intent suggestion:</span>
              <IntentBadge category={opp.intent_ai.category} source="ai" />
            </div>
            <ul className="text-xs text-muted-foreground list-disc pl-4">
              {(opp.intent_ai.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            {opp.intent_ai.objections?.length > 0 && (
              <p className="text-xs"><span className="text-muted-foreground">Objections:</span> {opp.intent_ai.objections.join("; ")}</p>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Button data-testid="ai-summary-button" size="sm" variant="outline" disabled={busy} onClick={() => run("summary")}>
            {busy === "summary" ? "Generating…" : "Generate summary"}
          </Button>
          {opp && (
            <Button data-testid="ai-intent-button" size="sm" variant="outline" disabled={busy} onClick={() => run("intent")}>
              {busy === "intent" ? "Analysing…" : "Suggest intent"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function Customer360Page() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { hasCap, user } = useAuth();
  const [data, setData] = useState(null);
  const [mergedInto, setMergedInto] = useState(null);
  const [selectedOppId, setSelectedOppId] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [intentOverride, setIntentOverride] = useState(false);
  const [intentCat, setIntentCat] = useState("warm");
  const [c360Cases, setC360Cases] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [skipTask, setSkipTask] = useState(null);
  const [skipReason, setSkipReason] = useState("");
  const [completeTask, setCompleteTask] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  // 2026-09 R13 stabilization (live-audit item D-7) — a leadership_
  // approval task was being offered as a direct "Complete" here (and
  // actually submitted as action="complete"), which the backend correctly
  // rejects (can_manually_act_on_task) — the task then stayed Open with no
  // pending approval, exactly the live audit's own reproduction. Mirrors
  // TasksPage.jsx's own already-correct submit_for_approval dialog.
  const [submitTask, setSubmitTask] = useState(null);
  const [submitNote, setSubmitNote] = useState("");
  // "Open customer tasks" (the task-review prompt / bell) links here with ?tab=tasks.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(requestedTab || "timeline");
  useEffect(() => { if (requestedTab) setActiveTab(requestedTab); }, [requestedTab]);
  const [mentorRefreshTick, setMentorRefreshTick] = useState(0);
  const tabsRef = useRef(null);
  const goToMentorTab = () => {
    setActiveTab("mentor");
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // `forceReselectAwayFrom` (opportunity_id, optional): passed when an
  // action just removed/discontinued the CURRENTLY selected opportunity
  // (e.g. "Remove product") — the opportunity still exists in the
  // reloaded list (discontinuing never deletes it), so the ordinary
  // "still exists -> keep it selected" check below would otherwise leave
  // Customer 360 sitting on the just-discontinued product pursuit.
  const load = useCallback((forceReselectAwayFrom) => {
    api.get(`/contacts/${contactId}/full`).then((r) => {
      if (r.data.merged) {
        // This customer id was merged into another, surviving record —
        // it must never render as if it were still an independent
        // customer. Redirect straight to the canonical one (`replace` so
        // the merged id never lands in browser back-history).
        setMergedInto(r.data);
        toast.info(`This customer was merged into ${r.data.merged_into_customer_id} — opening that record`);
        navigate(`/customers/${r.data.merged_into_contact_id}`, { replace: true });
        return;
      }
      setData(r.data);
      setSelectedOppId((prev) => {
        const stillValid = prev && prev !== forceReselectAwayFrom
          && r.data.opportunities.some((o) => o.opportunity_id === prev);
        return stillValid ? prev
          : (r.data.opportunities.find((o) => o.lifecycle_status === "active") || r.data.opportunities[0])?.opportunity_id;
      });
    });
    api.get(`/cases?contact_id=${contactId}`).then((r) => setC360Cases(r.data)).catch(() => {});
  }, [contactId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (hasCap("booking.assign")) {
      api.get("/users").then((r) => setCounsellors(r.data.users.filter((u) => u.is_active && ["counsellor", "supervisor", "manager", "admin"].includes(u.role)))).catch(() => {});
    }
  }, [hasCap]);

  const taskAct = async (taskId, action, reason, note) => {
    try {
      await api.post(`/tasks/${taskId}/action`, { action, reason, note });
      toast.success(action === "complete" ? "Task completed"
        : action === "reopen" ? "Task reopened"
        : action === "submit_for_approval" ? "Submitted for Leadership approval"
        : "Task skipped");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const completeTaskClick = (t) => {
    if (t.workflow?.task_key && t.case_id) {
      // A Process workflow task is completed only through its outcome form
      // on the Case (server-validated) — never a generic Complete.
      navigate(`/process/${t.case_id}?outcome_task=${t.task_id}`);
      return;
    }
    if (SYSTEM_VERIFIABLE_TASK_TYPES.has(t.task_type)) {
      taskAct(t.task_id, "complete");
      return;
    }
    // 2026-09 R13 stabilization (item D-7) — SalesOS can't independently
    // verify this task, so there is no direct Complete at all for it: only
    // "submit for Leadership review" (can_manually_act_on_task's own rule,
    // mirrored here so the button never triggers a guaranteed 403).
    if (t.completion_policy === "leadership_approval") {
      setSubmitTask(t); setSubmitNote("");
      return;
    }
    setCompleteTask(t); setCompleteNote("");
  };

  if (!data) {
    return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  }

  const { contact } = data;
  const opp = data.opportunities.find((o) => o.opportunity_id === selectedOppId);
  // A `cross_product` task (server-flagged false-Signup residue) is never the Next Action.
  const nextTask = pickNextTask(data.tasks, opp);
  // R15: when every open task for this pursuit is a Signup-derived step the canonical journey says is not current, the Next action is the journey's own immediate action (same text as "What happens next")
  const journeyOnly = !nextTask ? (data.tasks || []).find((t) => t.status === "open" && t.journey_stale && (!opp || t.opportunity_id === opp.opportunity_id)) : null;
  const activeAppt = data.appointments.find((a) => ["booked", "rescheduled"].includes(a.status));

  const genLink = async () => {
    try {
      const { data: d } = await api.post(`/appointments/booking-link/${opp.opportunity_id}`);
      const url = `${window.location.origin}${d.path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Booking link copied — share it with the customer", { description: url });
      load();
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const saveIntent = async () => {
    try {
      await api.patch(`/opportunities/${opp.opportunity_id}/intent`, { category: intentCat });
      toast.success("Intent updated (manual override)");
      setIntentOverride(false);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4" data-testid="customer-360">
      <BackButton fallbackRoute="/customers" testId="c360-back" className="-mb-1 -ml-2" />
      {/* Header — identity + selected opportunity state (spec: always obvious) */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-52">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 data-testid="c360-name" className="font-heading text-xl font-semibold">{contact.full_name}</h1>
                <TestBadge show={contact.is_test_record} />
                <span data-testid="c360-customer-id" className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{contact.customer_id}</span>
              </div>
              <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                {contact.primary_phone && (
                  <a href={`tel:${contact.primary_phone}`} data-testid="c360-phone-tel" className="flex items-center gap-1 hover:text-[hsl(var(--info))] hover:underline">
                    <Phone className="h-3 w-3" />{contact.primary_phone}
                    {contact.whatsapp_phone && contact.whatsapp_phone !== contact.primary_phone && (
                      <span className="text-[10px] text-[hsl(var(--success))]">· WA {contact.whatsapp_phone}</span>
                    )}
                  </a>
                )}
                {contact.email && (
                  <a href={`mailto:${contact.email}`} data-testid="c360-email-link" className="flex items-center gap-1 hover:text-[hsl(var(--info))] hover:underline">
                    <Mail className="h-3 w-3" />{contact.email}
                  </a>
                )}
                {contact.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{contact.city}</span>}
              </div>
              {(() => {
                const cm = data.contact_methods || { phones: [], emails: [] };
                const altP = (cm.phones || []).filter((p) => !p.is_primary);
                const altE = (cm.emails || []).filter((e) => !e.is_primary);
                if (!altP.length && !altE.length) return null;
                return (
                  <div className="mt-1 text-[11px] text-muted-foreground flex flex-wrap gap-x-3" data-testid="c360-alt-contact-methods">
                    {altP.map((p) => <span key={p.id || p.value}>Alt phone: {p.value}{p.is_whatsapp ? " (WhatsApp)" : ""}</span>)}
                    {altE.map((e) => <span key={e.id || e.value}>Alt email: {e.value}</span>)}
                  </div>
                );
              })()}
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                {/* Edit profile now lives in the primary action row (QuickActions)
                    so it's as easy to find as Call / Email. */}
                {hasCap("approvals.request") && contact.status !== "merged" && <MergeCustomerAction contact={contact} />}
              </div>
            </div>
            {opp && (
              <div className="flex flex-wrap items-center gap-2" data-testid="c360-opp-summary">
                <Badge variant="outline">{opp.product_name}</Badge>
                <LifecycleBadge status={opp.lifecycle_status} />
                {opp.booking_state === "human_followup_required"
                  ? null  /* shown as the canonical Voice state badge instead — not duplicated */
                  : <BookingStateBadge state={opp.booking_state} />}
                {opp.voice_state && opp.voice_state.label !== "AI not applicable"
                  && <VoiceStateBadge vs={opp.voice_state} showOwnership />}
                <IntentBadge category={opp.intent_manual?.category || opp.intent_ai?.category} source={opp.intent_manual ? "manual" : (opp.intent_ai ? "ai" : null)} />
                <span className="text-xs text-muted-foreground">Owner: <b>{opp.owner_name || "Pool (unclaimed)"}</b></span>
              </div>
            )}
          </div>
          {/* One canonical Tags control (2026-09 correction), scoped to the
              currently-selected opportunity — re-renders with that
              opportunity's own tags whenever `opp` changes (switching the
              selected product pursuit above). */}
          <TagsPanel opportunityId={opp?.opportunity_id} opportunityTags={opp?.tags || []}
            opportunityLabel={opp?.product_name} />
          <QuickActions contact={contact} opp={opp} onBook={() => setBookOpen(true)} onRefresh={load}
            opportunities={data.opportunities} cases={c360Cases} contactMethods={data.contact_methods} />
          <div className="mt-2">
            {/* canDirectOwnerChange is crm.assign-driven, not the dead
                owner.change capability (owner.change backed no real
                endpoint — 2026-09 fix) — this is the same capability that
                actually gates OpportunityLeadershipActions's real "Assign"
                button below, so the two controls can never both show at
                once for the same user. */}
            <ApprovalRequests contactId={contactId} opportunities={data.opportunities}
              canRequest={hasCap("approvals.request")} canDirectOwnerChange={hasCap("crm.assign")}
              canReallocatePayment={hasCap("finance.record")}
              selectedOppId={selectedOppId} />
          </div>
          {opp?.voice_state && opp.voice_state.label !== "AI not applicable" && (
            <div className="mt-3 flex items-center gap-2 text-sm bg-muted/60 rounded-md px-3 py-2" data-testid="c360-voice-state">
              <Bot className="h-4 w-4" />
              <span className="text-xs uppercase font-semibold text-muted-foreground">AI Voice / automation</span>
              <VoiceStateBadge vs={opp.voice_state} showOwnership />
              {opp.voice_state.owns_outreach && (
                <span className="text-xs text-muted-foreground">
                  AI still owns outreach — assigning an owner does not stop it; use “Take over from AI” on Leads or Calls to interrupt.
                </span>
              )}
              {opp.voice_state.next_attempt_at && (
                <span className="text-xs text-muted-foreground ml-auto">next attempt {fmtIST(opp.voice_state.next_attempt_at)}</span>
              )}
            </div>
          )}
          {(() => {
            // THE canonical Next Action (Workstream B / B3) for a registry-
            // governed Process Case of the selected opportunity — derived
            // live by the server, identical to the Case page's own.
            const govCase = (c360Cases || []).find((cc) => cc.governed && cc.next_action
              && (!opp || cc.opportunity_id === opp.opportunity_id));
            return govCase ? (
              <div className="mt-3">
                <NextActionCard action={govCase.next_action} testId="c360-next-action" onChanged={load} />
              </div>) : null;
          })()}
          {journeyOnly && !(c360Cases || []).some((cc) => cc.governed && cc.next_action && (!opp || cc.opportunity_id === opp.opportunity_id)) && (
            <div className="mt-3 flex items-center gap-2 text-sm bg-[hsl(var(--accent))]/60 rounded-md px-3 py-2" data-testid="c360-next-action-journey">
              <ChevronRight className="h-4 w-4" />
              <span className="text-xs uppercase font-semibold text-muted-foreground">Next action</span>
              <span className="font-medium">{journeyOnly.journey_immediate_action || "Review the Signup journey"}</span>
              <span className="text-xs text-muted-foreground ml-auto">from the Signup journey — see “What happens next”</span>
            </div>
          )}
          {nextTask && !(c360Cases || []).some((cc) => cc.governed && cc.next_action
            && (!opp || cc.opportunity_id === opp.opportunity_id)) && (
            <div className="mt-3 flex items-center gap-2 text-sm bg-[hsl(var(--accent))]/60 rounded-md px-3 py-2" data-testid="c360-next-action">
              <ChevronRight className="h-4 w-4" />
              <span className="text-xs uppercase font-semibold text-muted-foreground">Next action</span>
              <span className="font-medium">{nextTask.title}</span>
              <span className="text-xs text-muted-foreground ml-auto">due {fmtIST(nextTask.effective_due_at || nextTask.due_at, { time: false })}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* grid-cols-1 = minmax(0, 1fr) and min-w-0 on both columns (2026-09
          R6 mobile pass): below lg the implicit `auto` track grew to the
          widest child's min-content, widening the whole page past a phone's
          viewport; the track is now capped at the available width. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column — opportunities + booking actions */}
        <div className="space-y-4 min-w-0">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Opportunities</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.opportunities.map((o) => (
                <button
                  key={o.opportunity_id}
                  data-testid={`c360-opp-${o.opportunity_id}`}
                  onClick={() => setSelectedOppId(o.opportunity_id)}
                  className={`w-full text-left p-2.5 rounded-md border transition-colors ${o.opportunity_id === selectedOppId ? "border-[hsl(var(--ring))] bg-[hsl(var(--accent))]/40" : "hover:bg-muted"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{o.product_name}</span>
                    <LifecycleBadge status={o.lifecycle_status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Stage: {o.current_stage_name || "—"} · {o.owner_name || "Pool"}</div>
                </button>
              ))}
            </CardContent>
          </Card>

          {opp && hasCap("booking.manage") && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Booking actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {activeAppt ? (
                  <div className="text-sm bg-muted rounded-md p-2.5" data-testid="c360-active-appt">
                    <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4" /><span className="font-medium">{fmtIST(activeAppt.scheduled_start, { year: true })} IST</span></div>
                    <div className="text-xs text-muted-foreground mt-1">{activeAppt.appointment_type_name} · {activeAppt.counsellor_name || "Unassigned"}</div>
                  </div>
                ) : (
                  <>
                    <Button data-testid="c360-book-button" className="w-full" size="sm" onClick={() => setBookOpen(true)}>
                      <CalendarClock className="h-4 w-4 mr-1.5" /> Book consultation
                    </Button>
                    <Button data-testid="c360-booking-link-button" variant="outline" className="w-full" size="sm" onClick={genLink}>
                      <Link2 className="h-4 w-4 mr-1.5" /> Copy self-booking link
                    </Button>
                    <p className="text-[11px] text-muted-foreground">If WhatsApp automation is off, share the link manually — every automated send shows its delivery status on the timeline.</p>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {opp && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center justify-between">
                  Intent
                  <Button data-testid="c360-intent-override-toggle" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setIntentOverride(!intentOverride)}>Override</Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex gap-2 items-center">
                  <IntentBadge category={opp.intent_manual?.category || opp.intent_ai?.category || "unclear"} source={opp.intent_manual ? "manual" : (opp.intent_ai ? "ai" : null)} />
                  {opp.intent_manual && <span className="text-[10px] text-muted-foreground">set by {opp.intent_manual.by}</span>}
                </div>
                {intentOverride && (
                  <div className="flex gap-2">
                    <Select value={intentCat} onValueChange={setIntentCat}>
                      <SelectTrigger data-testid="c360-intent-select" className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["high_intent", "hot", "warm", "cold", "unclear"].map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button data-testid="c360-intent-save" size="sm" onClick={saveIntent}>Save</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <MentorSnapshotTeaser contactId={contactId} opportunityId={opp?.opportunity_id}
            onOpenFull={goToMentorTab} refreshKey={mentorRefreshTick} />
        </div>

        {/* Right column — stage + tabs */}
        <div className="lg:col-span-2 space-y-4 min-w-0">
          {opp && (
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm font-heading">Pipeline — {opp.product_name}</CardTitle>
                <OpportunityLeadershipActions opp={opp} contact={contact} onChanged={load} />
              </CardHeader>
              <CardContent>
                <StageStepper opp={opp} contact={contact} onChanged={load} />
              </CardContent>
            </Card>
          )}

          {opp && <WhatHappensNextPanel opportunityId={opp.opportunity_id} />}

          <Tabs value={activeTab} onValueChange={setActiveTab} ref={tabsRef}>
            <TabsList className="flex-wrap h-auto">
              {hasCap("ai.mentor.view") && (
                <TabsTrigger data-testid="c360-tab-mentor" value="mentor" className="gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" />Sales Mentor
                </TabsTrigger>
              )}
              <TabsTrigger data-testid="c360-tab-timeline" value="timeline">Timeline</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-profile" value="profile">Profile</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-calls" value="calls">Calls</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-comms" value="comms">Comms</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-tasks" value="tasks">Tasks ({data.tasks.filter((t) => t.status === "open").length})</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-appointments" value="appointments">Consultations ({data.appointments.length})</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-documents" value="documents">Documents</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-process" value="process">Process</TabsTrigger>
              <TabsTrigger data-testid="c360-tab-tickets" value="tickets">Tickets</TabsTrigger>
              {data.webinars?.length > 0 && (
                <TabsTrigger data-testid="c360-tab-webinars" value="webinars">Webinars ({data.webinars.length})</TabsTrigger>
              )}
              <TabsTrigger data-testid="c360-tab-notes" value="notes">Notes</TabsTrigger>
              {hasCap("finance.view") && <TabsTrigger data-testid="c360-tab-finance" value="finance">Finance</TabsTrigger>}
              <TabsTrigger data-testid="c360-tab-history" value="history">History</TabsTrigger>
            </TabsList>

            {hasCap("ai.mentor.view") && (
              <TabsContent value="mentor">
                <SalesMentorPanel contactId={contactId} opportunityId={opp?.opportunity_id}
                  productName={opp?.product_name}
                  intent={opp?.intent_manual?.category || opp?.intent_ai?.category}
                  onBriefingChanged={() => setMentorRefreshTick((t) => t + 1)}
                  onLegacyFallback={<AiPanel contactId={contactId} opp={opp} aiOutputs={data.ai_outputs} onRefresh={load} />} />
              </TabsContent>
            )}
            <TabsContent value="notes"><NotesTab contactId={contactId} canAdd={hasCap("notes.add")} /></TabsContent>
            {hasCap("finance.view") && (
              <TabsContent value="finance">
                <FinanceTab contactId={contactId} opportunities={data.opportunities}
                  canRecord={hasCap("finance.record")} canVerify={hasCap("finance.verify")}
                  canDocs={hasCap("finance.docs")} canConfig={hasCap("finance.config")}
                  canHistoricalMigration={hasCap("finance.historical_migration")}
                  onChanged={() => load()} />
              </TabsContent>
            )}

            <TabsContent value="profile"><ProfileTab contact={contact} opp={opp} onRefresh={load} leadIntakes={data.lead_intakes} /></TabsContent>
            <TabsContent value="calls"><CallsTab interactions={data.interactions} /></TabsContent>
            <TabsContent value="comms"><CommsTab interactions={data.interactions} communications={data.communications} /></TabsContent>
            <TabsContent value="documents"><DocumentsWorkspace contactId={contactId} opportunities={data.opportunities} cases={c360Cases} /></TabsContent>
            <TabsContent value="process"><CasesTab contactId={contactId} caseTimelines={data.case_timelines || {}} /></TabsContent>
            <TabsContent value="tickets"><TicketsTab contactId={contactId} /></TabsContent>
            <TabsContent value="webinars"><WebinarsTab webinars={data.webinars} /></TabsContent>

            <TabsContent value="timeline">
              {data.communications?.length > 0 && (
                <Card className="mb-3" data-testid="c360-communications">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <Send className="h-4 w-4" /> Automated communications
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    {data.communications.slice(0, 8).map((c) => (
                      <div key={c.outbox_id} className="flex items-center gap-2 text-sm flex-wrap" data-testid={`c360-comm-${c.outbox_id}`}>
                        <span className="w-24 shrink-0 text-xs text-muted-foreground">{fmtIST(c.created_at)}</span>
                        <span className="flex-1 min-w-40">
                          {commLabel(c)}
                          {commMetaDetails(c).length > 0 && (
                            <span className="block text-[11px] text-muted-foreground" data-testid={`c360-comm-meta-${c.outbox_id}`}>
                              {commMetaDetails(c).join(" · ")}
                              {c.meta?.annotations?.length > 0 && ` · Note: ${c.meta.annotations[0].note}`}
                            </span>
                          )}
                        </span>
                        <CommStatusBadge status={c.status} />
                        {c.status === "failed" && c.last_error && (
                          <span className="text-[11px] text-muted-foreground truncate max-w-56">{c.last_error}</span>
                        )}
                      </div>
                    ))}
                    <p className="text-[11px] text-muted-foreground pt-1">“Pending provider” means the message is safely queued and will send automatically once the integration is switched on.</p>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  {data.interactions.length === 0 ? <EmptyState icon={MessageSquare} title="No activity yet" />
                    : data.interactions.map((i) => (
                      <div key={i.interaction_id} className="flex gap-3 text-sm" data-testid={`timeline-item-${i.interaction_id}`}>
                        <div className="w-20 sm:w-28 shrink-0 text-xs text-muted-foreground pt-0.5">{fmtIST(i.created_at)}</div>
                        <div className="flex-1 min-w-0 break-words border-l pl-3 pb-1">
                          <span className="text-[10px] uppercase font-semibold text-muted-foreground mr-2">{i.type}</span>
                          <span className="text-xs text-muted-foreground mr-2" data-testid={`timeline-actor-${i.interaction_id}`}>{i.actor_label}</span>
                          <span className="whitespace-pre-line">{i.summary}</span>
                        </div>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tasks">
              <Card>
                <CardContent className="pt-4 space-y-2">
                  <TaskReviewBanner contactId={contactId} onChanged={load} />
                  {data.tasks.length === 0 ? <EmptyState title="No tasks" />
                    : data.tasks.map((t) => (
                      <div key={t.task_id} className="flex items-center gap-3 p-2 rounded-md border flex-wrap" data-testid={`c360-task-${t.task_id}`}>
                        <div className="flex-1 min-w-40">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{t.title}</span>
                            {t.is_mandatory && <span className="text-[10px] uppercase text-[hsl(var(--warning))] font-semibold">Mandatory</span>}
                            {t.journey_stale && <span data-testid={`c360-task-journey-stale-${t.task_id}`} className="text-[10px] uppercase text-muted-foreground font-semibold border rounded px-1" title={t.journey_stale_text}>Not current</span>}
                          </div>
                          {t.journey_stale && <p className="text-[11px] text-muted-foreground">{t.journey_stale_text}</p>}
                          <span className="text-xs text-muted-foreground">Due {fmtIST(t.due_at, { time: false })}{t.skip_reason ? (t.closure?.system_decided ? ` · ${t.skip_reason}` : ` · skip reason: ${t.skip_reason}`) : ""}</span>
                        </div>
                        <TaskBadge status={t.status} />
                        {t.status === "open" ? (
                          <div className="flex gap-1.5">
                            <Button data-testid={`c360-task-complete-${t.task_id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => completeTaskClick(t)}>
                              <Check className="h-3.5 w-3.5 mr-1" />
                              {t.workflow?.task_key ? "Open outcome form"
                                : t.completion_policy === "leadership_approval" ? "Submit for approval"
                                : "Complete"}
                            </Button>
                            <Button data-testid={`c360-task-skip-${t.task_id}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setSkipTask(t); setSkipReason(""); }}>
                              <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
                            </Button>
                          </div>
                        ) : ["completed", "skipped"].includes(t.status) && (
                          <Button data-testid={`c360-task-reopen-${t.task_id}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => taskAct(t.task_id, "reopen")}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
                          </Button>
                        )}
                      </div>
                    ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="appointments">
              <Card>
                <CardContent className="pt-4 space-y-2">
                  {data.appointments.length === 0 ? <EmptyState icon={CalendarClock} title="No appointments yet" />
                    : data.appointments.map((a) => {
                      const isActive = ["booked", "rescheduled"].includes(a.status);
                      return (
                        <div key={a.appointment_id} className="p-2.5 rounded-md border" data-testid={`c360-appt-${a.appointment_id}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{fmtIST(a.scheduled_start, { year: true })} IST</span>
                            <ApptBadge status={a.status} />
                            {isActive && <CalSyncBadge status={a.calendar_sync_status} />}
                            <span className="text-xs text-muted-foreground">{a.appointment_type_name} · {a.counsellor_name || "Unassigned"}{a.reschedule_count > 0 ? ` · rescheduled ×${a.reschedule_count}` : ""}</span>
                          </div>
                          {isActive && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <ConsultationActions
                                appt={{ ...a, contact_name: contact.full_name, customer_id: contact.customer_id }}
                                counsellors={counsellors} onSaved={load} canAddMeetLink={hasCap("booking.manage")} />
                            </div>
                          )}
                          {a.outcome_note && <p className="text-xs text-muted-foreground mt-1">Outcome: {a.outcome_note}</p>}
                          {a.status === "cancelled" && (
                            <div className="text-xs text-muted-foreground mt-1 space-y-0.5" data-testid={`c360-appt-cancel-detail-${a.appointment_id}`}>
                              <p>
                                {a.cancelled_by_type === "customer" ? "Cancelled by customer"
                                  : a.cancelled_by_type === "system" ? "Cancelled automatically"
                                  : `Cancelled by: ${a.cancelled_by_name || "—"}`}
                                {a.cancelled_at ? ` · ${fmtIST(a.cancelled_at)}` : ""}
                              </p>
                              {a.cancel_reason && <p>Reason: {a.cancel_reason}</p>}
                              {a.cancellation_note && <p>Note: {a.cancellation_note}</p>}
                            </div>
                          )}
                          {a.reschedule_count > 0 && a.last_rescheduled_at && (
                            <div className="text-xs text-muted-foreground mt-1 space-y-0.5" data-testid={`c360-appt-reschedule-detail-${a.appointment_id}`}>
                              <p>
                                Rescheduled ×{a.reschedule_count} · last by {a.rescheduled_by_name || (a.rescheduled_by_type === "customer" ? "customer" : "—")}
                                {a.reschedule_previous_start ? ` · from ${fmtIST(a.reschedule_previous_start)}` : ""}
                              </p>
                              {a.reschedule_reason && <p>Reason: {a.reschedule_reason}</p>}
                            </div>
                          )}
                          {a.history?.length > 1 && (
                            <p className="text-[11px] text-muted-foreground mt-1">History: {a.history.map((h) => h.event).join(" → ")}</p>
                          )}
                        </div>
                      );
                    })}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card>
                <CardContent className="pt-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><History className="h-4 w-4" /> Stage history</h3>
                    {data.stage_history.length === 0 ? <p className="text-xs text-muted-foreground">No stage changes yet.</p>
                      : data.stage_history.map((h) => (
                        <div key={h.history_id} className="text-sm flex gap-3 py-1">
                          <span className="w-28 shrink-0 text-xs text-muted-foreground">{fmtIST(h.at)}</span>
                          <span>{h.from_stage_name || "—"} → <b>{h.to_stage_name}</b>{h.override && <Badge variant="outline" className="ml-2 text-[10px] bg-[hsl(var(--warning))]/15">override</Badge>}{h.reason ? ` · ${h.reason}` : ""}</span>
                        </div>
                      ))}
                  </div>
                  <Separator />
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Lead intakes (attribution preserved)</h3>
                    {data.lead_intakes.map((l) => (
                      <div key={l.lead_id} className="py-1" data-testid={`c360-lead-intake-${l.lead_id}`}>
                        <div className="text-sm flex gap-3">
                          <span className="w-28 shrink-0 text-xs text-muted-foreground">{fmtIST(l.created_at)}</span>
                          <span>{l.product_code} · {l.source}{l.campaign ? ` / ${l.campaign}` : ""} · <span className="text-muted-foreground">{l.dedup_result?.replace(/_/g, " ")}</span></span>
                        </div>
                        <div className="ml-28 mt-1">
                          <LeadFormInfo leadIntakes={[l]} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {opp && <BookDialog opp={opp} open={bookOpen} setOpen={setBookOpen} onBooked={load} />}

      <Dialog open={!!skipTask} onOpenChange={(o) => !o && setSkipTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Skip task</DialogTitle></DialogHeader>
          <DialogDescription>A reason is required and recorded in the audit trail.</DialogDescription>
          <Textarea data-testid="c360-task-skip-reason" placeholder="Why is this task being skipped?" value={skipReason} onChange={(e) => setSkipReason(e.target.value)} />
          <DialogFooter>
            <Button data-testid="c360-task-skip-confirm" disabled={!skipReason.trim()}
              onClick={() => { taskAct(skipTask.task_id, "skip", skipReason); setSkipTask(null); }}>
              Skip with reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completeTask} onOpenChange={(o) => !o && setCompleteTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Complete task</DialogTitle></DialogHeader>
          <DialogDescription>SalesOS can't independently verify this task — describe what was actually done.</DialogDescription>
          <Textarea data-testid="c360-task-complete-note" placeholder="What did you do to complete this?" value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} />
          <DialogFooter>
            <Button data-testid="c360-task-complete-confirm" disabled={!completeNote.trim()}
              onClick={() => { taskAct(completeTask.task_id, "complete", null, completeNote); setCompleteTask(null); }}>
              Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-09 R13 stabilization (item D-7) — mirrors TasksPage.jsx's own
          already-correct submit_for_approval flow for a leadership_approval
          task; this dialog never calls action="complete", which the
          backend always rejects for this task type. */}
      <Dialog open={!!submitTask} onOpenChange={(o) => !o && setSubmitTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Submit for approval</DialogTitle></DialogHeader>
          <DialogDescription>This task requires Leadership approval — describe what was done so Leadership can review it.</DialogDescription>
          <Textarea data-testid="c360-task-submit-note" placeholder="What did you do?" value={submitNote} onChange={(e) => setSubmitNote(e.target.value)} />
          <DialogFooter>
            <Button data-testid="c360-task-submit-confirm" disabled={!submitNote.trim()}
              onClick={() => { taskAct(submitTask.task_id, "submit_for_approval", null, submitNote); setSubmitTask(null); }}>
              Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
